import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlane, faTrain, faBus, faCar, faHotel, faUtensils, faTicket, faChevronDown, faShip } from '@fortawesome/free-solid-svg-icons';

const KLOOK_AID = import.meta.env.VITE_KLOOK_AID || "116395";

const getKlookUrl = (query) => {
  const params = new URLSearchParams({
    search_landing: 'true',
    query: `Explore ${query}`.trim(),
    aid: KLOOK_AID,
    utm_medium: 'affiliate-alwayson',
    utm_source: 'non-network',
    utm_campaign: KLOOK_AID,
    utm_term: '',
  });
  return `https://www.klook.com/search/result/?${params.toString()}`;
};

const get12GoUrl = (fromCity, toCity, date, travelers = 2) => {
  const from = encodeURIComponent(fromCity.toLowerCase().replace(/\s+/g, '-'));
  const to = encodeURIComponent(toCity.toLowerCase().replace(/\s+/g, '-'));
  const adults = travelers || 2;

  let url = `https://12go.asia/en/travel/${from}/${to}`;
  const params = new URLSearchParams({
    people: adults.toString(),
    adults: adults.toString(),
    direction: 'forward',
    vehclasses_tab: 'all'
  });

  if (date) {
    params.set('date', date);
  }

  return `${url}?${params.toString()}`;
};

const BookingDropdownButton = ({ icon, text, options, bgClass, textClass, borderClass }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, openUpward: false });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpward = spaceBelow < 220 && spaceAbove > spaceBelow;

      setDropdownPosition({
        top: openUpward ? rect.top - 8 : rect.bottom + 4,
        left: Math.max(8, rect.right - 200),
        openUpward
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleScroll = () => setIsOpen(false);

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`px-3 py-1.5 ${bgClass} ${textClass} ${borderClass} rounded-full text-xs font-medium hover:opacity-90 transition-colors flex items-center gap-1.5 cursor-pointer`}
      >
        {icon && <FontAwesomeIcon icon={icon} />}
        <span>{text}</span>
        <FontAwesomeIcon icon={faChevronDown} className={`text-[10px] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/10"
            style={{ zIndex: 99998 }}
            onClick={() => setIsOpen(false)}
          />
          <div
            ref={dropdownRef}
            className="fixed bg-white rounded-xl shadow-2xl border border-gray-200 py-2 min-w-[200px]"
            style={{
              zIndex: 99999,
              top: dropdownPosition.openUpward ? 'auto' : dropdownPosition.top,
              bottom: dropdownPosition.openUpward ? `${window.innerHeight - dropdownPosition.top}px` : 'auto',
              left: dropdownPosition.left,
            }}
          >
            {options.map((option, idx) => (
              <a
                key={idx}
                href={option.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors"
                onClick={() => setIsOpen(false)}
              >
                {option.icon && <span className="w-5 text-center text-base">{option.icon}</span>}
                <span className="whitespace-nowrap">{option.label}</span>
              </a>
          ))}
          </div>
        </>
      )}
    </>
  );
};

const getFirstService = (localServices, category) => {
  if (!localServices || !localServices[category] || !localServices[category].length) {
    return null;
  }
  const local = localServices[category].find(s => s.isLocal);
  return local || localServices[category][0];
};

const shortenCityName = (name) => {
  if (!name) return '';
  const city = name.split(',')[0].trim();
  return city.length > 15 ? city.substring(0, 12) + '...' : city;
};

const ActivityBookingButton = ({ activity, bookingLinks, tripSummary, localServices, travelPreferences, dayDate, dayCity, isLastDay = false }) => {
  const cityForBooking = (activity?.toCity || dayCity || tripSummary?.destination || '').toString();
  const cityForBookingShort = cityForBooking.split(/[,+]/)[0].trim();
  const fromCity = shortenCityName(tripSummary?.startingFrom);
  const toCity = shortenCityName(tripSummary?.destination);
  const routeText = fromCity && toCity ? `${fromCity} → ${toCity}` : '';

  const actFromCity = activity.fromCity ? shortenCityName(activity.fromCity) : fromCity;
  const actToCity   = activity.toCity   ? shortenCityName(activity.toCity)   : toCity;
  const actRouteText = actFromCity && actToCity ? `${actFromCity} → ${actToCity}` : routeText;

  const airportCodes    = bookingLinks?.flights?.airportCodes || {};
  const hasAirportCodes = !!(airportCodes.fromCode && airportCodes.toCode);

  const stationCodes    = bookingLinks?.trains?.stationCodes || {};
  const hasStationCodes = !!(stationCodes.fromCode && stationCodes.toCode);

  const startDate = tripSummary?.startDate || '';
  const endDate = tripSummary?.endDate || '';

  const activityDate = activity?.date || dayDate || startDate || '';

  const travelers = tripSummary?.travelers || tripSummary?.numberOfTravelers || 2;

  const formatDateForSkyscanner = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${y.slice(2)}${m}${d}`;
  };

  const formatDateForMMT = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  if (activity.category === 'transport') {
    const titleLower = activity.title?.toLowerCase() || '';
    const descLower = activity.description?.toLowerCase() || '';

    const isLocalTransfer = activity.isTransfer ||
                            activity.transferType === 'arrival' ||
                            activity.transferType === 'departure' ||
                            titleLower.includes('transfer to hotel') ||
                            titleLower.includes('transfer for departure') ||
                            (titleLower.includes('hotel') && titleLower.includes('transfer'));

    const isInterCityTransfer = !isLocalTransfer && (
                                 activity.isInterCity ||
                                 (activity.fromCity && activity.toCity) ||
                                 (titleLower.includes('transfer') && titleLower.match(/from\s+\w+\s+to\s+\w+/)));

    const isArrivalDeparture = activity.isArrival ||
                               activity.isDeparture ||
                               titleLower.includes('arrival in') ||
                               titleLower.includes('departure to');

    const getSelectedModes = () => {
      if (activity.travelModes) return activity.travelModes;
      if (isInterCityTransfer && travelPreferences?.travelBetween?.length > 0) {
        return travelPreferences.travelBetween;
      }
      if (travelPreferences?.travelToDest) return travelPreferences.travelToDest;
      if (travelPreferences?.travelBetween) return travelPreferences.travelBetween;
      return [];
    };
    const selectedModes = getSelectedModes();
    const modesArray = Array.isArray(selectedModes) ? selectedModes : [selectedModes].filter(Boolean);

    if (isLocalTransfer) {
      const cityName = cityForBookingShort || 'Destination';

      const localTransferOptions = [
        { label: 'Search on Google', url: `https://www.google.com/search?q=taxi+transfer+${encodeURIComponent(cityName)}`, icon: '🔍' },
        { label: 'Uber', url: 'https://www.uber.com', icon: '🚗' },
        { label: 'Ola Cabs', url: 'https://www.olacabs.com', icon: '🚕' },
        { label: 'Klook Transfers', url: getKlookUrl('airport transfer ' + cityName), icon: '🎫' },
        { label: 'Google Maps', url: `https://www.google.com/maps/search/taxi+${encodeURIComponent(cityName)}`, icon: '📍' },
      ];

      return (
        <div className="flex gap-2 flex-wrap">
          <BookingDropdownButton
            icon={faCar}
            text="Book Cab"
            options={localTransferOptions}
            bgClass="bg-purple-100"
            textClass="text-purple-700"
            borderClass="border border-purple-200"
          />
          <BookingDropdownButton
            icon={faBus}
            text="Local Bus"
            options={[
              { label: 'Search on Google', url: `https://www.google.com/search?q=local+bus+${encodeURIComponent(cityName)}`, icon: '🔍' },
              { label: 'Google Maps Transit', url: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(cityName)}&travelmode=transit`, icon: '🚌' },
            ]}
            bgClass="bg-green-100"
            textClass="text-green-700"
            borderClass="border border-green-200"
          />
        </div>
      );
    }

    if (isInterCityTransfer && modesArray.length > 0) {
      const interCityFrom = activity.fromCity || tripSummary?.destination?.split(',')[0]?.trim() || '';
      const interCityTo = activity.toCity || '';
      const routeLabel = interCityFrom && interCityTo ? `${interCityFrom} → ${interCityTo}` : 'Inter-city Transfer';

      const bookingButtons = [];

      modesArray.forEach((mode, idx) => {
        const modeLower = mode?.toLowerCase() || '';

        if (modeLower.includes('flight') || modeLower === 'flights') {
          const flightOptions = [
            { label: 'Search on Google', url: `https://www.google.com/travel/flights?q=Flights+from+${encodeURIComponent(interCityFrom)}+to+${encodeURIComponent(interCityTo)}`, icon: '🔍' },
            { label: 'Skyscanner', url: `https://www.skyscanner.com/transport/flights/${encodeURIComponent(interCityFrom)}/${encodeURIComponent(interCityTo)}/`, icon: '✈️' },
            { label: 'Kayak', url: `https://www.kayak.com/flights/${encodeURIComponent(interCityFrom)}-${encodeURIComponent(interCityTo)}/`, icon: '🛫' },
          ];
          bookingButtons.push(
            <BookingDropdownButton
              key={`intercity-flight-${idx}`}
              icon={faPlane}
              text="Flight"
              options={flightOptions}
              bgClass="bg-blue-100"
              textClass="text-blue-700"
              borderClass="border border-blue-200"
            />
          );
        }

        if (modeLower.includes('train') || modeLower === 'trains') {
          const trainOptions = [
            { label: 'Search on Google', url: `https://www.google.com/search?q=train+${encodeURIComponent(interCityFrom)}+to+${encodeURIComponent(interCityTo)}`, icon: '🔍' },
            { label: '12Go Asia', url: get12GoUrl(interCityFrom, interCityTo, activityDate, travelers), icon: '🚂' },
            { label: 'Trainline', url: `https://www.trainline.com/search/${encodeURIComponent(interCityFrom)}/${encodeURIComponent(interCityTo)}`, icon: '🎫' },
            { label: 'IRCTC (India)', url: 'https://www.irctc.co.in', icon: '🇮🇳' },
          ];
          bookingButtons.push(
            <BookingDropdownButton
              key={`intercity-train-${idx}`}
              icon={faTrain}
              text="Train"
              options={trainOptions}
              bgClass="bg-orange-100"
              textClass="text-orange-700"
              borderClass="border border-orange-200"
            />
          );
        }

        if (modeLower.includes('bus') || modeLower === 'buses') {
          const busOptions = [
            { label: 'Search on Google', url: `https://www.google.com/search?q=bus+${encodeURIComponent(interCityFrom)}+to+${encodeURIComponent(interCityTo)}`, icon: '🔍' },
            { label: '12Go Asia', url: get12GoUrl(interCityFrom, interCityTo, activityDate, travelers), icon: '🚌' },
            { label: 'RedBus', url: `https://www.redbus.in/bus-tickets/${encodeURIComponent(interCityFrom.toLowerCase())}-to-${encodeURIComponent(interCityTo.toLowerCase())}`, icon: '🎫' },
            { label: 'FlixBus', url: `https://www.flixbus.com/bus-routes?from=${encodeURIComponent(interCityFrom)}&to=${encodeURIComponent(interCityTo)}`, icon: '🚍' },
          ];
          bookingButtons.push(
            <BookingDropdownButton
              key={`intercity-bus-${idx}`}
              icon={faBus}
              text="Bus"
              options={busOptions}
              bgClass="bg-green-100"
              textClass="text-green-700"
              borderClass="border border-green-200"
            />
          );
        }

        if (modeLower.includes('cab') || modeLower.includes('taxi') || modeLower.includes('car') ||
            modeLower === 'road' || modeLower === 'drive' || modeLower.includes('road')) {
          const cabOptions = [
            { label: 'Search on Google', url: `https://www.google.com/search?q=cab+${encodeURIComponent(interCityFrom)}+to+${encodeURIComponent(interCityTo)}`, icon: '🔍' },
            { label: 'Klook', url: getKlookUrl('transfer ' + interCityFrom + ' to ' + interCityTo), icon: '🎫' },
            { label: 'Savaari', url: `https://www.savaari.com/outstation-cabs/${encodeURIComponent(interCityFrom.toLowerCase())}-to-${encodeURIComponent(interCityTo.toLowerCase())}`, icon: '🚕' },
            { label: 'Uber', url: 'https://www.uber.com', icon: '🚗' },
          ];
          bookingButtons.push(
            <BookingDropdownButton
              key={`intercity-cab-${idx}`}
              icon={faCar}
              text="Cab"
              options={cabOptions}
              bgClass="bg-purple-100"
              textClass="text-purple-700"
              borderClass="border border-purple-200"
            />
          );
        }

        if (modeLower.includes('ferry') || modeLower.includes('boat')) {
          const ferryOptions = [
            { label: 'Search on Google', url: `https://www.google.com/search?q=ferry+${encodeURIComponent(interCityFrom)}+to+${encodeURIComponent(interCityTo)}`, icon: '🔍' },
            { label: '12Go Asia', url: get12GoUrl(interCityFrom, interCityTo, activityDate, travelers), icon: '⛴️' },
            { label: 'Direct Ferries', url: `https://www.directferries.com/search?origin=${encodeURIComponent(interCityFrom)}&destination=${encodeURIComponent(interCityTo)}`, icon: '🚢' },
            { label: 'Ferryhopper', url: `https://www.ferryhopper.com/en/search?from=${encodeURIComponent(interCityFrom)}&to=${encodeURIComponent(interCityTo)}`, icon: '🛳️' },
          ];
          bookingButtons.push(
            <BookingDropdownButton
              key={`intercity-ferry-${idx}`}
              icon={faShip}
              text="Ferry"
              options={ferryOptions}
              bgClass="bg-cyan-100"
              textClass="text-cyan-700"
              borderClass="border border-cyan-200"
            />
          );
        }
      });

      let hotelButton = null;
      if (interCityTo && !isLastDay) {
        const encodedCity = encodeURIComponent(interCityTo);

        const formatDateForHotelMMT = (dateStr) => {
          if (!dateStr) return '';
          const [y, m, d] = dateStr.split('-');
          return `${m}${d}${y}`;
        };

        const checkInMMT = formatDateForHotelMMT(startDate);
        const checkOutMMT = formatDateForHotelMMT(endDate);

        const hotelOptions = [
          { label: 'Search on Google', url: `https://www.google.com/travel/hotels/${encodedCity}`, icon: '🔍' },
          { label: 'Booking.com', url: `https://www.booking.com/searchresults.html?ss=${encodedCity}&checkin=${startDate}&checkout=${endDate}`, icon: '🏨' },
          { label: 'Agoda', url: `https://www.agoda.com/search?checkIn=${startDate}&checkOut=${endDate}&rooms=1&adults=2&textToSearch=${encodedCity}`, icon: '🌏' },
          { label: 'MakeMyTrip', url: `https://www.makemytrip.com/hotels/hotel-listing/?checkin=${checkInMMT}&checkout=${checkOutMMT}&roomStayQualifier=1e2e0&locusType=city&searchText=${encodedCity}&country=IN`, icon: '🎫' },
        ];

        hotelButton = (
          <BookingDropdownButton
            key="intercity-hotel"
            icon={faHotel}
            text={`Hotels in ${interCityTo}`}
            options={hotelOptions}
            bgClass="bg-teal-100"
            textClass="text-teal-700"
            borderClass="border border-teal-200"
          />
        );
      }

      if (bookingButtons.length > 0 || hotelButton) {
        return (
          <div className="flex flex-row items-end justify-end gap-3 sm:gap-4 w-full overflow-x-auto pb-1 scrollbar-hidden">
            {bookingButtons.length > 0 && (
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className="text-[10px] sm:text-xs text-gray-500 font-medium whitespace-nowrap">{routeLabel}</span>
                <div className="flex gap-2">{bookingButtons}</div>
              </div>
            )}
            {hotelButton && (
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className="text-[10px] sm:text-xs text-gray-500 font-medium whitespace-nowrap">Stay in {interCityTo}</span>
                <div className="flex gap-2">{hotelButton}</div>
              </div>
            )}
          </div>
        );
      }
    }

    if (isArrivalDeparture && modesArray.length > 0) {
      const bookingButtons = [];

      modesArray.forEach((mode, idx) => {
        const modeLower = mode?.toLowerCase() || '';

        if (modeLower.includes('flight') || modeLower === 'flights') {
          const departSkyscanner = formatDateForSkyscanner(startDate);
          const departMMT = formatDateForMMT(startDate);

          const googleFlightsUrl = `https://www.google.com/travel/flights?q=Flights+from+${encodeURIComponent(actFromCity)}+to+${encodeURIComponent(actToCity)}+on+${startDate}`;

          const flightOptions = [
            { label: 'Search on Google Flights', url: googleFlightsUrl, icon: '🔍' },
          ];
          if (hasAirportCodes) {
            flightOptions.push(
              {
                label: 'Book on Skyscanner',
                url: `https://www.skyscanner.co.in/transport/flights/${airportCodes.fromCode.toLowerCase()}/${airportCodes.toCode.toLowerCase()}/${departSkyscanner}/`,
                icon: '✈️',
              },
              {
                label: 'View on Kayak',
                url: `https://www.kayak.co.in/flights/${airportCodes.fromCode}-${airportCodes.toCode}/${startDate}${endDate ? '/' + endDate : ''}?sort=bestflight_a`,
                icon: '🛫',
              },
              {
                label: 'MakeMyTrip',
                url: `https://www.makemytrip.com/flight/search?itinerary=${airportCodes.fromCode}-${airportCodes.toCode}-${departMMT}&tripType=${endDate ? 'R' : 'O'}&paxType=A-1_C-0_I-0&intl=false&cabinClass=E`,
                icon: '🎫',
              },
            );
          }

          bookingButtons.push(
            <BookingDropdownButton
              key={`flight-${idx}`}
              icon={faPlane}
              text={actRouteText || 'Book Flight'}
              options={flightOptions}
              bgClass="bg-blue-100"
              textClass="text-blue-700"
              borderClass="border border-blue-200"
            />
          );
        } else if (modeLower.includes('train') || modeLower === 'trains') {
          const trainOptions = [
            { label: 'Search on Google', url: `https://www.google.com/search?q=train+${encodeURIComponent(actFromCity)}+to+${encodeURIComponent(actToCity)}`, icon: '🔍' },
          ];
          if (hasStationCodes) {
            trainOptions.push(
              {
                label: 'IRCTC',
                url: `https://www.irctc.co.in/nget/train-search?fromStation=${stationCodes.fromCode}&toStation=${stationCodes.toCode}&jDate=${startDate}&class=SL&quota=GN`,
                icon: '🚂',
              },
              {
                label: 'MakeMyTrip Trains',
                url: `https://www.makemytrip.com/railways/srp/?fromStationCode=${stationCodes.fromCode}&toStationCode=${stationCodes.toCode}&departDate=${startDate}&class=SL`,
                icon: '🎫',
              },
            );
          }
          trainOptions.push(
            { label: '12Go Asia', url: get12GoUrl(actFromCity, actToCity, activityDate, travelers), icon: '🌏' },
          );
          bookingButtons.push(
            <BookingDropdownButton
              key={`train-${idx}`}
              icon={faTrain}
              text={actRouteText || 'Book Train'}
              options={trainOptions}
              bgClass="bg-orange-100"
              textClass="text-orange-700"
              borderClass="border border-orange-200"
            />
          );
        } else if (modeLower.includes('bus') || modeLower === 'buses') {
          const busOptions = [
            { label: 'Search on Google', url: `https://www.google.com/search?q=bus+${encodeURIComponent(actFromCity)}+to+${encodeURIComponent(actToCity)}`, icon: '🔍' },
            { label: 'RedBus', url: `https://www.redbus.in/bus-tickets/${encodeURIComponent(actFromCity.toLowerCase())}-to-${encodeURIComponent(actToCity.toLowerCase())}`, icon: '🚌' },
            { label: 'AbhiBus', url: `https://www.abhibus.com/bus-tickets/${encodeURIComponent(actFromCity)}-to-${encodeURIComponent(actToCity)}`, icon: '🎫' },
            { label: 'MakeMyTrip Bus', url: `https://www.makemytrip.com/bus-tickets/${encodeURIComponent(actFromCity)}-${encodeURIComponent(actToCity)}`, icon: '🚍' },
          ];
          bookingButtons.push(
            <BookingDropdownButton
              key={`bus-${idx}`}
              icon={faBus}
              text={actRouteText || 'Book Bus'}
              options={busOptions}
              bgClass="bg-green-100"
              textClass="text-green-700"
              borderClass="border border-green-200"
            />
          );
        } else if (modeLower.includes('road') || modeLower === 'road' || modeLower.includes('car')) {
          const roadOptions = [
            { label: 'Google Maps Route', url: `https://www.google.com/maps/dir/${encodeURIComponent(actFromCity)}/${encodeURIComponent(actToCity)}`, icon: '🗺️' },
            { label: 'Self Drive (Zoomcar)', url: `https://www.zoomcar.com/in/self-drive-car-rental/${encodeURIComponent(actFromCity.toLowerCase())}`, icon: '🚗' },
            { label: 'Savaari (Outstation)', url: `https://www.savaari.com/outstation-cabs/${encodeURIComponent(actFromCity.toLowerCase())}-to-${encodeURIComponent(actToCity.toLowerCase())}`, icon: '🚕' },
          ];
          bookingButtons.push(
            <BookingDropdownButton
              key={`road-${idx}`}
              icon={faCar}
              text={actRouteText || 'View Route'}
              options={roadOptions}
              bgClass="bg-gray-100"
              textClass="text-gray-700"
              borderClass="border border-gray-200"
            />
          );
        } else if (modeLower.includes('ferry') || modeLower.includes('boat')) {
          const ferryOptions = [
            { label: 'Search on Google', url: `https://www.google.com/search?q=ferry+${encodeURIComponent(actFromCity)}+to+${encodeURIComponent(actToCity)}`, icon: '🔍' },
            { label: '12Go Asia', url: get12GoUrl(actFromCity, actToCity, activityDate, travelers), icon: '⛴️' },
            { label: 'Direct Ferries', url: `https://www.directferries.com/search?origin=${encodeURIComponent(actFromCity)}&destination=${encodeURIComponent(actToCity)}`, icon: '🚢' },
            { label: 'Ferryhopper', url: `https://www.ferryhopper.com/en/search?from=${encodeURIComponent(actFromCity)}&to=${encodeURIComponent(actToCity)}`, icon: '🛳️' },
          ];
          bookingButtons.push(
            <BookingDropdownButton
              key={`ferry-${idx}`}
              icon={faShip}
              text={actRouteText || 'Book Ferry'}
              options={ferryOptions}
              bgClass="bg-cyan-100"
              textClass="text-cyan-700"
              borderClass="border border-cyan-200"
            />
          );
        }
      });

      const transferCity = activity.isArrival ? actToCity : actFromCity;
      const transferOptions = [
        { label: 'Search on Google', url: `https://www.google.com/search?q=airport+hotel+transfer+${encodeURIComponent(transferCity)}`, icon: '🔍' },
        { label: 'Klook Transfers', url: getKlookUrl('airport transfer ' + transferCity), icon: '🎫' },
        { label: 'Uber', url: `https://www.uber.com`, icon: '🚗' },
        { label: 'Ola Cabs', url: `https://www.olacabs.com`, icon: '🚕' },
        { label: 'Google Maps', url: `https://www.google.com/maps/search/taxi+${encodeURIComponent(transferCity)}`, icon: '📍' },
      ];

      bookingButtons.push(
        <BookingDropdownButton
          key="transfer"
          icon={faCar}
          text="Book Transfer"
          options={transferOptions}
          bgClass="bg-purple-600"
          textClass="text-white"
          borderClass=""
        />
      );

      if (bookingButtons.length > 0) {
        return <div className="flex gap-2 flex-wrap">{bookingButtons}</div>;
      }
    }

    const isAirportRelated = titleLower.includes('airport') ||
                             descLower.includes('airport') ||
                             titleLower.includes('flight') ||
                             descLower.includes('flight');

    const isTrainJourney = (titleLower.includes('train') ||
                            titleLower.includes('railway') ||
                            descLower.includes('railway')) &&
                           !isAirportRelated;

    if (isAirportRelated) {
      const airportCodes = bookingLinks?.flights?.airportCodes;
      const hasAirportCodes = airportCodes?.fromCode && airportCodes?.toCode;

      const departSkyscanner = formatDateForSkyscanner(startDate);
      const departMMT = formatDateForMMT(startDate);

      const googleFlightsUrl = bookingLinks?.flights?.googleFlights ||
        `https://www.google.com/travel/flights?q=Flights+from+${encodeURIComponent(fromCity)}+to+${encodeURIComponent(toCity)}+on+${startDate}`;

      const flightOptions = [
        { label: 'Search on Google Flights', url: googleFlightsUrl, icon: '🔍' },
      ];
      if (hasAirportCodes || bookingLinks?.flights?.skyscanner ||
          bookingLinks?.flights?.kayak || bookingLinks?.flights?.makemytrip) {
        if (bookingLinks?.flights?.skyscanner || hasAirportCodes) {
          flightOptions.push({
            label: 'Book on Skyscanner',
            url: bookingLinks?.flights?.skyscanner ||
              `https://www.skyscanner.co.in/transport/flights/${airportCodes.fromCode.toLowerCase()}/${airportCodes.toCode.toLowerCase()}/${departSkyscanner}/`,
            icon: '✈️',
          });
        }
        if (bookingLinks?.flights?.kayak || hasAirportCodes) {
          flightOptions.push({
            label: 'View on Kayak',
            url: bookingLinks?.flights?.kayak ||
              `https://www.kayak.co.in/flights/${airportCodes.fromCode}-${airportCodes.toCode}/${startDate}${endDate ? '/' + endDate : ''}?sort=bestflight_a`,
            icon: '🛫',
          });
        }
        if (bookingLinks?.flights?.makemytrip || hasAirportCodes) {
          flightOptions.push({
            label: 'MakeMyTrip',
            url: bookingLinks?.flights?.makemytrip ||
              `https://www.makemytrip.com/flight/search?itinerary=${airportCodes.fromCode}-${airportCodes.toCode}-${departMMT}&tripType=${endDate ? 'R' : 'O'}&paxType=A-1_C-0_I-0&intl=false&cabinClass=E`,
            icon: '🎫',
          });
        }
      }

      return (
        <BookingDropdownButton
          icon={faPlane}
          text={routeText || 'Book Flight'}
          options={flightOptions}
          bgClass="bg-blue-100"
          textClass="text-blue-700"
          borderClass="border border-blue-200"
        />
      );
    }

    if (isTrainJourney) {
      const irctcUrl = hasStationCodes
        ? `https://www.irctc.co.in/nget/train-search?fromStation=${stationCodes.fromCode}&toStation=${stationCodes.toCode}&jDate=${startDate}&class=SL&quota=GN`
        : 'https://www.irctc.co.in';
      const trainOptions = [
        { label: 'Search on Google', url: `https://www.google.com/search?q=train+${encodeURIComponent(fromCity)}+to+${encodeURIComponent(toCity)}`, icon: '🔍' },
        { label: 'IRCTC', url: irctcUrl, icon: '🚂' },
        { label: '12Go Asia', url: get12GoUrl(fromCity, toCity, activityDate, travelers), icon: '🌏' },
      ];

      return (
        <BookingDropdownButton
          icon={faTrain}
          text={routeText || 'Book Train'}
          options={trainOptions}
          bgClass="bg-orange-100"
          textClass="text-orange-700"
          borderClass="border border-orange-200"
        />
      );
    }

    const cityName = cityForBookingShort || 'Destination';
    const rideshareServices = localServices?.rideshare || [];

    const transferOptions = [
      { label: 'Search on Google', url: `https://www.google.com/search?q=cab+taxi+transfer+${encodeURIComponent(cityName)}`, icon: '🔍' },
      { label: 'Klook Transfers', url: getKlookUrl('airport transfer ' + cityName), icon: '🎫' },
      { label: 'Uber', url: `https://www.uber.com`, icon: '🚗' },
      { label: 'Ola Cabs', url: `https://www.olacabs.com`, icon: '🚕' },
    ];

    rideshareServices.forEach(service => {
      if (!transferOptions.find(opt => opt.label.toLowerCase().includes(service.name.toLowerCase()))) {
        transferOptions.push({
          label: service.name,
          url: service.url,
          icon: service.name.toLowerCase().includes('grab') ? '🚗' :
                service.name.toLowerCase().includes('lyft') ? '🚕' : '🚖'
        });
      }
    });

    transferOptions.push({ label: 'Google Maps', url: `https://www.google.com/maps/search/taxi+${encodeURIComponent(cityName)}`, icon: '📍' });

    return (
      <BookingDropdownButton
        icon={faCar}
        text="Book Transfer"
        options={transferOptions}
        bgClass="bg-purple-600"
        textClass="text-white"
        borderClass=""
      />
    );
  }

  if (activity.category === 'accommodation') {
    if (activity.isCheckIn || activity.isCheckOut) {
      return null;
    }
    if (isLastDay) {
      return null;
    }

    const shortLocation = cityForBookingShort || 'Destination';
    const encodedLocation = encodeURIComponent(shortLocation);
    const destination = shortLocation;

    const formatDateForMMT = (dateStr) => {
      if (!dateStr) return '';
      const [y, m, d] = dateStr.split('-');
      return `${m}${d}${y}`;
    };
    const getLOS = () => {
      if (!startDate || !endDate) return 1;
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : 1;
    };

    const checkInMMT = formatDateForMMT(startDate);
    const checkOutMMT = formatDateForMMT(endDate);

    const hotelOptions = [
      { label: 'Search on Google', url: `https://www.google.com/travel/hotels/${encodedLocation}`, icon: '🔍' },
      { label: 'Booking.com', url: bookingLinks?.hotels?.booking || `https://www.booking.com/searchresults.html?ss=${encodedLocation}&checkin=${startDate}&checkout=${endDate}`, icon: '🏨' },
      { label: 'Agoda', url: `https://www.agoda.com/search?checkIn=${startDate}&checkOut=${endDate}&rooms=1&adults=2&children=0&los=${getLOS()}&textToSearch=${encodeURIComponent(destination)}&priceCur=INR`, icon: '🌏' },
      { label: 'TripAdvisor', url: `https://www.tripadvisor.com/Search?q=${encodedLocation}&searchSessionId=hotels`, icon: '⭐' },
    ];
    if (bookingLinks?.hotels?.airbnb) {
      hotelOptions.push({ label: 'Airbnb', url: bookingLinks.hotels.airbnb, icon: '🏠' });
    }
    hotelOptions.push({
      label: 'MakeMyTrip',
      url: `https://www.makemytrip.com/hotels/hotel-listing/?checkin=${checkInMMT}&checkout=${checkOutMMT}&roomStayQualifier=1e2e0&locusType=city&searchText=${encodeURIComponent(shortLocation)}&country=IN`,
      icon: '🎫'
    });

    return (
      <BookingDropdownButton
        icon={faHotel}
        text={`Hotels in ${shortLocation || 'Destination'}`}
        options={hotelOptions}
        bgClass="bg-teal-100"
        textClass="text-teal-700"
        borderClass="border border-teal-200"
      />
    );
  }

  if (activity.category === 'sightseeing' || activity.category === 'adventure') {
    const activityName = activity.title || 'activity';
    const destination = tripSummary?.destination || '';
    const searchQuery = `${activityName} ${destination}`.trim();

    const activityOptions = [
      {
        label: 'Book on Klook',
        url: getKlookUrl(searchQuery),
        icon: '🎫'
      },
      {
        label: 'Search on Google',
        url: `https://www.google.com/search?q=${encodeURIComponent(searchQuery + ' tickets booking')}`,
        icon: '🔍'
      },
      {
        label: 'GetYourGuide',
        url: `https://www.getyourguide.com/s/?q=${encodeURIComponent(searchQuery)}`,
        icon: '🗺️'
      },
    ];

    return (
      <BookingDropdownButton
        icon={faTicket}
        text="Book Activity"
        options={activityOptions}
        bgClass="bg-teal-600"
        textClass="text-white"
        borderClass=""
      />
    );
  }

  if (activity.category === 'dining') {
    const destination = tripSummary?.destination || '';
    const cityName = destination.split(',')[0].trim().toLowerCase();
    const restaurantName = activity.title || 'restaurant';
    const searchQuery = `${restaurantName} ${destination}`;

    const diningOptions = [
      { label: 'Search on Google', url: `https://www.google.com/search?q=${encodeURIComponent(searchQuery + ' restaurant')}`, icon: '🔍' },
      { label: 'Zomato', url: `https://www.zomato.com/${cityName}/restaurants?q=${encodeURIComponent(restaurantName)}`, icon: '🍽️' },
      { label: 'Swiggy Dineout', url: `https://www.swiggy.com/dineout/${cityName}/restaurants?query=${encodeURIComponent(restaurantName)}`, icon: '🍴' },
      { label: 'Google Maps', url: `https://www.google.com/maps/search/${encodeURIComponent(searchQuery)}`, icon: '📍' },
    ];

    const tableService = getFirstService(localServices, 'tableBooking');

    if (tableService) {
      diningOptions.splice(1, 0, {
        label: `Reserve on ${tableService.name}`,
        url: tableService.url,
        icon: '📅'
      });
    }

    return (
      <BookingDropdownButton
        icon={faUtensils}
        text="Find Restaurant"
        options={diningOptions}
        bgClass="bg-red-600"
        textClass="text-white"
        borderClass=""
      />
    );
  }

  return null;
};

export default ActivityBookingButton;
